import { TERMS_VERSION } from "../content/termsAndConditionsContent";
import { getCurrentUserId } from "./healthInterestsStorage";

const ACCEPT_PREFIX = "mytara_terms_accept_v1";
const REGISTER_ACK_KEY = "mytara_terms_register_ack";

export interface TermsAcceptanceRecord {
  version: string;
  acceptedAt: string;
  source: "dashboard" | "registration";
}

function acceptKey(userId: string) {
  return `${ACCEPT_PREFIX}_${userId}`;
}

function readUserDataRecord(): Record<string, unknown> | null {
  const raw =
    localStorage.getItem("userData") || sessionStorage.getItem("userData");
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export function hasServerTermsAcceptance(): boolean {
  const data = readUserDataRecord();
  return data?.isTermAndConditionChecked === true;
}

export function markServerTermsAcceptanceInUserData(): void {
  const storages = [localStorage, sessionStorage];
  storages.forEach((storage) => {
    const raw = storage.getItem("userData");
    if (!raw) return;
    try {
      const data = JSON.parse(raw) as Record<string, unknown>;
      storage.setItem(
        "userData",
        JSON.stringify({
          ...data,
          isTermAndConditionChecked: true,
        })
      );
    } catch {
      // ignore corrupt profile cache
    }
  });
}

export function hasRegistrationTermsAcknowledgement(): boolean {
  try {
    const raw = sessionStorage.getItem(REGISTER_ACK_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.version === TERMS_VERSION && Boolean(parsed?.acknowledgedAt);
  } catch {
    return false;
  }
}

export function markRegistrationTermsAcknowledgement(): void {
  sessionStorage.setItem(
    REGISTER_ACK_KEY,
    JSON.stringify({
      version: TERMS_VERSION,
      acknowledgedAt: new Date().toISOString(),
    })
  );
}

export function clearRegistrationTermsAcknowledgement(): void {
  sessionStorage.removeItem(REGISTER_ACK_KEY);
}

export function getTermsAcceptance(userId: string | null): TermsAcceptanceRecord | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(acceptKey(userId));
    if (!raw) return null;
    return JSON.parse(raw) as TermsAcceptanceRecord;
  } catch {
    return null;
  }
}

export function hasAcceptedCurrentTerms(userId: string | null): boolean {
  const record = getTermsAcceptance(userId);
  return record?.version === TERMS_VERSION && Boolean(record?.acceptedAt);
}

export function saveTermsAcceptance(
  userId: string,
  source: TermsAcceptanceRecord["source"] = "dashboard"
): void {
  localStorage.setItem(
    acceptKey(userId),
    JSON.stringify({
      version: TERMS_VERSION,
      acceptedAt: new Date().toISOString(),
      source,
    } satisfies TermsAcceptanceRecord)
  );
}

export function shouldForceTermsAcceptance(): boolean {
  const userId = getCurrentUserId();
  if (!userId) return false;
  if (hasServerTermsAcceptance()) return false;
  return !hasAcceptedCurrentTerms(userId);
}
